import React from 'react';
import { RichTextDescription } from 'coop-adventure-engine';
import Button from '../ui/Button';
import { renderRichText, plainTextToRichText, isRichTextDescription } from '../../utils/richTextRenderer';

export interface OptionButtonProps {
  text: string | RichTextDescription;
  onClick: () => void;
  disabled?: boolean;
  waiting?: boolean;
  disabledReason?: string; // Optional reason why the option is disabled
  variant?: 'default' | 'primary' | 'secondary' | 'glass-primary' | 'glass-secondary' | 'glass-ghost';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  className?: string;
  customColor?: string; // Optional custom button color from parent
}

const OptionButton: React.FC<OptionButtonProps> = ({
  text,
  onClick,
  disabled = false,
  waiting = false,
  disabledReason,
  variant = 'default',
  size = 'md',
  fullWidth = true,
  className = '',
  customColor
}) => {
  const buttonVariant = variant === 'default' ? 'glass-secondary' : variant;
  
  // Render option text as rich text if it's RichTextDescription, otherwise as plain text
  const renderedText = isRichTextDescription(text) 
    ? renderRichText(text)
    : renderRichText(plainTextToRichText(text.replace(/^["']|["']$/g, '')));
  
  return (
    <div className={`space-y-2 ${className}`}>
      <Button
        variant={buttonVariant}
        size={size}
        fullWidth={fullWidth}
        disabled={disabled || waiting}
        onClick={onClick}
        {...(customColor ? { customColor } : {})}
        className="min-h-[48px] text-center justify-center"
        data-testid="game-option"
      >
        <div className="flex flex-col items-center w-full">
          <div 
            className="font-medium option-rich-text"
            dangerouslySetInnerHTML={{ __html: renderedText }}
          />
          {disabled && disabledReason && !waiting && (
            <span className="text-sm text-gray-500 mt-1 text-center">
              {disabledReason}
            </span>
          )}
        </div>
      </Button>
      
      {waiting && (
        <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
          <div className="animate-pulse w-2 h-2 bg-blue-500 rounded-full"></div>
          <div className="animate-pulse w-2 h-2 bg-blue-500 rounded-full" style={{ animationDelay: '0.2s' }}></div>
          <div className="animate-pulse w-2 h-2 bg-blue-500 rounded-full" style={{ animationDelay: '0.4s' }}></div>
        </div>
      )}
    </div>
  );
};

export default OptionButton;
